/** Generic icon of a POI category no rule matches. */
const DEFAULT_ICON = 'fa-location-dot';

/**
 * Font Awesome icon of each kind of POI category, as `[icon, pattern]` rules checked in order —
 * the first pattern found in the category wins, so the more specific rules come first (a
 * "Motorbike dealer" is not a car, a "Snack bar" is not a bar, a "Swimwear store" is clothing).
 * The categories are geodata's free-text `tier1_category` (around a thousand of them): the rules
 * group them into fewer than 50 icons. Word boundaries keep a rule from matching inside another
 * word ("toy" in Toyota, "tractor" in contractor, "pet" in carpet).
 */
const POI_CATEGORY_ICON_RULES: [string, RegExp][] = [
  // Transport first: "Motorbike dealer", "Truck dealer", "Boat dealer" or "Aircraft dealer" are not cars.
  ['fa-plane', /aircraft|airline|aviation|aerospace|aeronautical|helicopter|aerial sports|airport|parasailing/i],
  ['fa-motorcycle', /motorcycle|motorbike|scooter|\batv\b|moped/i],
  ['fa-seedling', /flower/i],
  [
    'fa-truck',
    /\btruck|\bvan\b|delivery|shipping|postal|courier|post office|mailbox|refrigerated transport|container|pallet|livery|haulage|removals/i
  ],
  [
    'fa-bus',
    /\bbus\b|coach|minibus|\btram\b|\btrains?\b|train (depot|repair)|railway|railroad|monorail|transit|public transport|transport hub|transportation|taxi/i
  ],
  [
    'fa-tractor',
    /\btractor|\bfarm|agricultur|orchard|dairy|flour mill|feed manufacturer|turf|pick your own|cider mill|land allotment|vegetable wholesale/i
  ],
  ['fa-horse', /horse|equestrian|pony|saddlery|dude ranch|\bpolo\b/i],
  [
    'fa-sailboat',
    /boat|sailing|yacht|marine|harbor|harbour|\bport\b|seaport|\bdock|ferry|cruise|whale|naval|canoe|kayak|rowing|coast guard/i
  ],
  ['fa-fish', /fishing|angler|hunting|aquarium shop/i],
  // Food and drink, before shops, sports and clubs: "Snack bar", "Sports bar", "Wine club", "Pie shop".
  [
    'fa-utensils',
    /restaurant|bistro|takeaway|takeout|noodle|kebab|caterer|snack|buffet|poke|piadina|ramen|durum|izakaya|soup kitchen|pantry|ham bar/i
  ],
  [
    'fa-martini-glass-citrus',
    /\bbar\b|night ?club|dance club|\bpub\b|beer|wine|cider bar|distillery|alcohol|adult entertainment|vineyard$/i
  ],
  ['fa-mug-hot', /cafe|coffee|\btea\b|chocolate/i],
  ['fa-cake-candles', /bakery|cookie|pancake|yogurt|sweets|dessert|pie shop|cake/i],
  [
    'fa-basket-shopping',
    /grocery|delicatessen|cured ham|spice|organic shop|health food|kosher|supermarket|soft drinks|food supply|meat|cannery|olive oil/i
  ],
  // Fashion, before industry and swimming: "Clothes and fabric manufacturer", "Swimwear store".
  [
    'fa-shirt',
    /cloth|fashion|tuxedo|kilt|lingerie|swimwear|formal wear|maternity|couture|knitwear|textile|\bfabrics?\b|\bwool\b|sewing|costume|\bdye\b|tailor|leather|\bshoe/i
  ],
  ['fa-gem', /jewel|clock|\bwatch(es)?\b|watchmaker/i],
  // Health and care.
  [
    'fa-spa',
    /\bspa\b|massage|sauna|aromatherapy|reflexolog|hot tub|public bath|yoga|meditation|tai chi|beauty|cosmetic|feng shui/i
  ],
  ['fa-scissors', /barber|\bhair|\bwig\b|piercing|tanning|nail salon/i],
  [
    'fa-stethoscope',
    /hospital|clinic|polyclinic|policlinic|medic|surgeon|surgery|physician|\w+ologist|\w+iatrist|allergist|therapist|osteopath|dietitian|\bnurse\b|health|dental|optical|\beye\b|contact lenses|hearing aid|blood|\bmri\b|\bhiv\b|\bstd\b|fertility|abortion|emergency (room|care)|walk-in|herbalist|pharmac|orthop|orthotic|prosthetic|acupuncture|hypnotherapy|rehabilitation|foot care|wheelchair|disability|diabetes|childbirth|psycho|counselor/i
  ],
  // Sport and outdoors.
  ['fa-person-swimming', /swim|aquatic|diving|\bdive\b|scuba|surf|water ski|water sports/i],
  [
    'fa-dumbbell',
    /fitness|\bgym\b|boot camp|weight loss|leisure centre|martial|karate|judo|kung fu|kickboxing|jujitsu|capoeira|boxing|shooting range|airsoft|paintball|laser tag/i
  ],
  [
    'fa-mountain-sun',
    /\bski|snow|toboggan|climbing|hiking|cliff|ridge|rocky|mountain|parkour|off.road|nudist|bird watching|wildlife|woods|dune|summer camp|caravan|picnic/i
  ],
  ['fa-bicycle', /bicycle|cycling|skate|skating/i],
  [
    'fa-trophy',
    /football|soccer|rugby|handball|hockey|cricket|baseball|softball|volleyball|tennis|padel|badminton|golf|bowling|billiard|billard|go-kart|race track|sports|trophy/i
  ],
  // Culture and leisure.
  ['fa-music', /music|piano|guitar|\bdrum|record (store|company)|\bdj\b|choir|vocal/i],
  ['fa-camera', /photograph|photo booth|photography|camera|portrait|video production|movie|cinema|\bfilm/i],
  [
    'fa-masks-theater',
    /theater|theatre|drama|performing arts|comedy|magician|flamenco|dance|circus|balloon artist|talent agency/i
  ],
  [
    'fa-palette',
    /\bart\b|\barts\b|artistic|painting|sculpt|stained glass|calligraphy|pottery|ceramic|handicraft|stone carving|embroidery|bookbinder/i
  ],
  [
    'fa-gamepad',
    /\bgames?\b|\btoys?\b|chess|bingo|trading card|comic|miniatures|model shop|hobby|collectible|amusement|puzzle/i
  ],
  [
    'fa-champagne-glasses',
    /event|wedding|bridal|marriage|festival|marquee|bouncy castle|balloon|entertainment|party|celebrant|christmas|souvenir|greeting|gift/i
  ],
  [
    'fa-landmark',
    /monument|landmark|heritage|histor|manor house|battle site|castle|attraction|observatory|archive|library|museum/i
  ],
  [
    'fa-place-of-worship',
    /church|mosque|monastery|abbey|convent|sanctuary|pilgrimage|worship|masonic|marae|temple|buddhist/i
  ],
  ['fa-book', /\bbooks?\b|newspaper|publish|\bmedia\b|\bpress\b|signwriting|\bpaper\b/i],
  // Public services and people.
  ['fa-paw', /\bpets?\b|\bdogs?\b|cattery|\bzoo\b|aquarium|animal/i],
  [
    'fa-hand-holding-heart',
    /charity|social|welfare|food bank|homeless|volunteer|non-profit|donation|support|foster|youth|senior|care service|home care|childminder|childcare|children's club|children hall|children's home|patients|community|cooperative|low income|citizen|consumer advice|retirement|job centre|nursery/i
  ],
  [
    'fa-graduation-cap',
    /research foundation|school|college|academy|institute|university|faculty|education|tutor|instructor|lesson|teacher|training|language|course|student|preschool|playgroup|class\b|camp$/i
  ],
  [
    'fa-shield-halved',
    /police|military|army|armed forces|barracks|defen[cs]e|prison|safety|security|investigator|fire|rescue|emergency/i
  ],
  [
    'fa-scale-balanced',
    /\blaw\b|lawyer|attorney|barrister|notar|legal|paralegal|courthouse|bankruptcy|small claims|loss adjuster/i
  ],
  [
    'fa-building-columns',
    /government|council|department|ministry|municipal|provincial|district|\btax\b|passport|pension|customs|state office|public works|bureau|information cent|tourism|office$/i
  ],
  // Nature and places.
  [
    'fa-seedling',
    /environment|natural resources|garden|landscap|arborist|tree service|lawn|conservatory supply|gazebo|park$|park and|country park|arboretum|wetland/i
  ],
  ['fa-water', /lake|\bsea\b|\bbay\b|reservoir|canal|spring|weir|reef|beach|\bsand\b|water|fountain|\bpier\b/i],
  ['fa-bed', /hotel|hostel|holiday|cottage|bungalow|cabin|accommodation|housing|\bbeds\b|residence/i],
  // Cars and roads.
  [
    'fa-car',
    /\bcars?\b|dealer|\bauto|vehicle|tyre|\btire|\bmot\b|brake|windshield|wrecker|\brv\b|motoring|driver|parking|valet|toll|weigh station|motorway|\broad\b|hub cap|diesel engine|limousine/i
  ],
  // Industry, energy, construction and home.
  [
    'fa-industry',
    /manufactur|factory|plant\b|refinery|chemical|industr|mill\b|meat packer|processor|engineer|biotechnology|\boil\b|\bgas\b|propane|fuel|energy|solar|photovoltaic|electric utility|generator|quarry|junkyard|salvage|garbage|reclamation|sewage|septic|water treatment|wholesaler|exporter|importer|distributor/i
  ],
  [
    'fa-hammer',
    /contractor|builder|construction|masonry|\btiles?\b|carpet|plaster|joiner|scaffold|paving|asphalt|excavat|insulation|hardware|diy|plumbing|\bdoors?\b|window|glazing|blinds|wallpaper|fence|gutter|millwork|stone|marble|aggregate|steel|ladder|floor|chimney|carport|garage builder|shopfitter|surveyor|topography|architect/i
  ],
  [
    'fa-couch',
    /furniture|\bbed\b|\brugs?\b|appliance|washer|glassware|wicker|interior|lighting|furnace|heating|home improvement|bathroom supply|home audio|home cinema|kitchen/i
  ],
  ['fa-laptop', /electronic|computer|data recovery|screen repair|\bgps\b|photocopier|software|telephone|audio visual/i],
  [
    'fa-briefcase',
    /consult|advis|finance|financial|credit union|building society|bank|insurance|recruit|real estate|estate|property|apprais|market research|public relations|business|office space|translation|holding|corporate|agency|company|firm|broker|organizer|association|auction|funeral|casket|superannuation|economic/i
  ],
  [
    'fa-screwdriver-wrench',
    /repair|restoration|maintenance|cleaning|key duplication|alteration|clearance|installer|installation|gasfitter|service$/i
  ],
  ['fa-store', /shop|store|supplier|showroom|retail|market|outlet|warehouse|vending|cannabis|tobacco|duty free/i]
];

/** Font Awesome classes of the icon of a geodata POI category (`tier1_category`). */
export function getPoiCategoryIcon(category: string): string {
  const icon = POI_CATEGORY_ICON_RULES.find(([, pattern]) => pattern.test(category))?.[0] ?? DEFAULT_ICON;

  return `fa-regular ${icon}`;
}

/** Every icon a POI category can get, the generic one included. */
export const POI_CATEGORY_ICONS = [...new Set([...POI_CATEGORY_ICON_RULES.map(([icon]) => icon), DEFAULT_ICON])].map(
  icon => `fa-regular ${icon}`
);
